import { useMemo } from "react";

interface Props {
  text: string;
  /** 0..1 — fraction of recording time elapsed. Drives which segment shows. */
  progress: number;
}

/**
 * Segment-based teleprompter — inspired by LLYLL's iOS RecordingView.
 *
 * - Splits the script by newlines; each non-empty line becomes one segment.
 * - Segments advance as `progress` goes from 0 to 1, equally distributed.
 * - Within a segment, markers of the form `...(placeholder text)...` are
 *   highlighted in llLemonade. Users fill in their own values in their head
 *   (name, job, etc.) while reading.
 * - Style: large text, centered, no scrolling. The current segment replaces
 *   the previous with a fade-in.
 */
export default function Teleprompter({ text, progress }: Props) {
  const segments = useMemo(
    () =>
      text
        .split(/\r?\n+/)
        .map((s) => s.trim())
        .filter(Boolean),
    [text]
  );

  if (segments.length === 0) return null;

  const clamped = Math.min(Math.max(progress, 0), 1);
  const idx = Math.min(
    Math.floor(clamped * segments.length),
    segments.length - 1
  );
  const segment = segments[idx];
  // Progress WITHIN the current segment (0..1) — drives the per-segment bar
  const segmentProgress = clamped >= 1 ? 1 : (clamped * segments.length) % 1;

  // Split segment into parts, separating out placeholder markers so we can
  // style them differently. Regex captures `...( ... )...`.
  const parts = segment.split(/(\.\.\.\([^)]*\)\.\.\.)/g).filter(Boolean);

  return (
    <div className="flex flex-col items-center justify-center h-full px-6 gap-4">
      {/* Segment counter + per-segment progress */}
      {segments.length > 1 && (
        <div className="w-full max-w-md flex items-center gap-3">
          <span className="text-ivory/60 text-xs font-mono tabular-nums">
            {idx + 1}/{segments.length}
          </span>
          <div className="flex-1 h-1 bg-white/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-lemonade transition-[width] duration-100 ease-linear"
              style={{ width: `${segmentProgress * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Current segment — `key={idx}` forces remount for fade-in */}
      <div
        key={idx}
        className="teleprompter-fade text-2xl md:text-4xl text-ivory font-semibold text-center leading-snug max-w-3xl"
      >
        {parts.map((part, i) => {
          const m = part.match(/^\.\.\.\(([^)]*)\)\.\.\.$/);
          if (m) {
            return (
              <span
                key={i}
                className="text-lemonade font-bold"
              >
                {m[1]}
              </span>
            );
          }
          return <span key={i}>{part}</span>;
        })}
      </div>
    </div>
  );
}
